"use client";

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Step {
  id: string;
  number: number;
  icon: string;
  title: string;
  description: string;
}

const HowItWorksSection = () => {
  const [visibleSteps, setVisibleSteps] = useState<number[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);

  const steps: Step[] = [
    {
      id: 'step_create',
      number: 1,
      icon: 'PencilSquareIcon',
      title: 'Create',
      description: 'Record letters, videos, or audio messages. Share your wisdom, love, and memories.',
    },
    {
      id: 'step_schedule',
      number: 2,
      icon: 'CalendarDaysIcon',
      title: 'Schedule',
      description: 'Pick a date, special occasion, or set to deliver upon passing. You control when.',
    },
    {
      id: 'step_delivered',
      number: 3,
      icon: 'PaperAirplaneIcon',
      title: 'Delivered',
      description: 'Your message reaches them automatically, exactly as you intended. Peace of mind guaranteed.',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setVisibleSteps((prev) => (prev.includes(index) ? prev : [...prev, index]));
          }
        });
      },
      { threshold: 0.2 }
    );

    const stepElements = sectionRef.current?.querySelectorAll('.step-card');
    stepElements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" ref={sectionRef} className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-6 uppercase tracking-wider">
            Simple Process
          </span>
          <h2 className="font-jakarta font-bold text-4xl md:text-5xl text-foreground mb-6 tracking-tight">
            How It Works
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
            Three simple steps to preserve your legacy and ensure your final words reach the people you love.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          {/* Connecting Lines (Desktop) */}
          <div className="hidden md:block absolute top-20 left-1/6 right-1/6 h-0.5 bg-gradient-to-r from-primary/30 via-secondary/30 to-accent/30" />

          {steps.map((step, index) => (
            <div
              key={step.id}
              data-index={index}
              className={`step-card bg-card rounded-3xl p-8 border border-border shadow-lg hover:shadow-xl transition-all duration-500 relative ${
                visibleSteps.includes(index)
                  ? 'opacity-100 translate-y-0' :'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Step Number Badge */}
              <div className="absolute -top-6 left-8">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center shadow-lg">
                  <span className="font-jakarta font-bold text-xl text-primary-foreground">
                    {step.number}
                  </span>
                </div>
              </div>

              {/* Icon */}
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 mt-4">
                <Icon name={step.icon as any} size={32} className="text-primary" />
              </div>

              {/* Content */}
              <h3 className="font-jakarta font-bold text-2xl text-foreground mb-4">{step.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{step.description}</p>

              {/* Arrow (Desktop) */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 text-primary/30">
                  <Icon name="ArrowRightIcon" size={32} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;